/** 플랫폼 유료 이벤트 이용권 카드 · Supabase */

import { supabase } from '../supabaseClient';
import type { AdminPaidEventPassProduct } from '../utils/adminPaidEventPassProducts';

function mapProduct(row: Record<string, unknown>): AdminPaidEventPassProduct {
  return {
    id: String(row.id),
    issuedAt: typeof row.issued_at === 'string' ? row.issued_at : new Date().toISOString(),
    eventName: typeof row.event_name === 'string' ? row.event_name : '(이름 없음)',
    flatAmountWon:
      typeof row.flat_amount_won === 'number' && Number.isFinite(row.flat_amount_won)
        ? Math.floor(row.flat_amount_won)
        : 3000,
    paidPlanStartMonth:
      typeof row.paid_plan_start_month === 'string' ? row.paid_plan_start_month : '',
    durationMonths:
      typeof row.duration_months === 'number' && Number.isFinite(row.duration_months)
        ? Math.floor(row.duration_months)
        : 1,
    planEndIso:
      typeof row.plan_end_iso === 'string'
        ? row.plan_end_iso.slice(0, 10)
        : String(row.plan_end_iso ?? '').slice(0, 10),
    activeUserCount:
      typeof row.active_user_count === 'number' && Number.isFinite(row.active_user_count)
        ? Math.floor(row.active_user_count)
        : 1,
    soldCount:
      typeof row.sold_count === 'number' && Number.isFinite(row.sold_count)
        ? Math.floor(row.sold_count)
        : 0,
    saleStatus: row.sale_status === 'closed' ? 'closed' : 'active',
  };
}

function toInsertRow(p: AdminPaidEventPassProduct): Record<string, unknown> {
  return {
    id: p.id,
    event_name: p.eventName,
    flat_amount_won: p.flatAmountWon,
    paid_plan_start_month: p.paidPlanStartMonth,
    duration_months: p.durationMonths,
    plan_end_iso: p.planEndIso,
    active_user_count: p.activeUserCount,
    sold_count: p.soldCount,
    sale_status: p.saleStatus,
    issued_at: p.issuedAt,
    updated_at: new Date().toISOString(),
  };
}

/** 관리자: 전체 목록 */
export async function fetchPaidEventPassProductsAdmin(): Promise<AdminPaidEventPassProduct[]> {
  const { data, error } = await supabase
    .from('platform_paid_event_pass_products')
    .select('*')
    .order('issued_at', { ascending: false })
    .limit(400);
  if (error) throw error;
  return (data ?? []).map((r) => mapProduct(r as Record<string, unknown>));
}

/** 이용권 화면: 판매중만 */
export async function fetchActivePaidEventPassProducts(): Promise<AdminPaidEventPassProduct[]> {
  const { data, error } = await supabase
    .from('platform_paid_event_pass_products')
    .select('*')
    .eq('sale_status', 'active')
    .order('issued_at', { ascending: false })
    .limit(400);
  if (error) throw error;
  return (data ?? []).map((r) => mapProduct(r as Record<string, unknown>));
}

export async function insertPaidEventPassProduct(
  product: AdminPaidEventPassProduct,
): Promise<AdminPaidEventPassProduct> {
  const { data, error } = await supabase
    .from('platform_paid_event_pass_products')
    .insert(toInsertRow(product))
    .select('*')
    .single();
  if (error) throw error;
  return mapProduct(data as Record<string, unknown>);
}

export async function updatePaidEventPassProduct(
  id: string,
  patch: Partial<Omit<AdminPaidEventPassProduct, 'id'>>,
): Promise<AdminPaidEventPassProduct> {
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.eventName !== undefined) row.event_name = patch.eventName;
  if (patch.flatAmountWon !== undefined) row.flat_amount_won = patch.flatAmountWon;
  if (patch.paidPlanStartMonth !== undefined) row.paid_plan_start_month = patch.paidPlanStartMonth;
  if (patch.durationMonths !== undefined) row.duration_months = patch.durationMonths;
  if (patch.planEndIso !== undefined) row.plan_end_iso = patch.planEndIso;
  if (patch.activeUserCount !== undefined) row.active_user_count = patch.activeUserCount;
  if (patch.soldCount !== undefined) row.sold_count = patch.soldCount;
  if (patch.saleStatus !== undefined) row.sale_status = patch.saleStatus;
  if (patch.issuedAt !== undefined) row.issued_at = patch.issuedAt;

  const { data, error } = await supabase
    .from('platform_paid_event_pass_products')
    .update(row)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return mapProduct(data as Record<string, unknown>);
}

export async function deletePaidEventPassProductRemote(id: string): Promise<void> {
  const { error } = await supabase.from('platform_paid_event_pass_products').delete().eq('id', id);
  if (error) throw error;
}

export async function incrementPaidEventProductSoldRemote(
  id: string,
): Promise<AdminPaidEventPassProduct> {
  const { data, error } = await supabase.rpc('increment_paid_event_product_sold', { p_id: id });
  if (error) throw error;
  return mapProduct(data as Record<string, unknown>);
}

/** localStorage → DB 1회 이전(비어 있을 때만) */
export async function importPaidEventProductsFromLocalIfEmpty(
  localRows: AdminPaidEventPassProduct[],
): Promise<AdminPaidEventPassProduct[]> {
  const existing = await fetchPaidEventPassProductsAdmin();
  if (existing.length > 0 || localRows.length === 0) return existing;
  const { error } = await supabase
    .from('platform_paid_event_pass_products')
    .insert(localRows.map(toInsertRow));
  if (error) throw error;
  return fetchPaidEventPassProductsAdmin();
}
